import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import Modal, { ModalProps } from '../../components/Modal'
import Radom from '../../state/Radom'
import User from '../../state/User'
import { RADOM_COLORS } from '../../util/Constants'
import { DepositMethods } from '../../util/Managed'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { filter, flattenDeep } from 'lodash'

type AccountModalProps = ModalProps & {
  onAccountCreated: () => void
}

enum AccountType {
  Crypto,
  Fiat
}

enum FiatAccountType {
  USD = 'USD',
  EUR = 'EUR',
}

const CreateDepositAccountModal = observer(({ ...props }: AccountModalProps): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [accountType, setAccountType] = useState<AccountType>(AccountType.Crypto)

  // Crypto options
  const [selectedDepositMethods, setSelectedDepositMethods] = useState<string[]>([])

  // Fiat options
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountType>()

  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()

  const createCryptoAccount = async (): Promise<void> => {
    if (selectedDepositMethods.length <= 0) {
      return
    }

    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Crypto: {
            depositMethods: selectedDepositMethods
              .filter(m => m.includes('_'))
              .map(m => ({
                network: m.split('_')[0],
                token: m.split('_')[1] == 'undefined' ? undefined : m.split('_')[1]
              }))
          }
        }
      })
      props.onAccountCreated()
      successToast('Deposit account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const createFiatAccount = async (): Promise<void> => {
    if (!fiatAccountType) {
      errorToast('')
      return
    }

    if (!accountType) {
      return
    }

    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Fiat: {
            currency: fiatAccountType
          }
        }
      })
      props.onAccountCreated()
      successToast('Deposit account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const createAccount = async (): Promise<void> => {
    if (accountType === AccountType.Crypto) {
      return await createCryptoAccount()
    } else {
      return await createFiatAccount()
    }
  }

  const renderCryptoForm = (): ReactElement => (
    <>
      <TextInputWithLabel
        label='Name'
        placeholder='Enter a name for this account'
        tooltip='Enter an identifiable name, e.g. your user&apos;s unique ID'
        disabled={isLoading}
        required
        value={accountName}
        onChange={e => {
          const el = (e.target as HTMLInputElement)
          const value = el.value
          setAccountName(value)
        }}
      />
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
        <InputLabel>Deposit methods</InputLabel>
        <Dropdown
          innerStyle={{ maxHeight: 300 }}
          disabled={isLoading}
          selectedContent={
            selectedDepositMethods.length > 0
              ? <span style={{ fontSize: 14 }}>{selectedDepositMethods.filter(t => t.includes('_')).length} methods selected</span>
              : <span style={{ fontSize: 14 }}>Select deposit methods</span>
          }
          dropdownContent={
            DepositMethods
              .filter(t => testMode ? t.isTestnet : !t.isTestnet)
              .map(m =>
                <>
                  <DropdownItem
                    onClick={() => {
                      const methods = filter(flattenDeep([m.name, m.subMethods?.map(m => [
                        m.method ? `${m.method.network}_${m.method.token}` : m.name,
                        m.subMethods?.map(n => `${n.method.network}_${n.method.token}`)])
                      ]), m => !!m);
                      let methodsToRemove = methods, methodsToAdd = methods

                      if (selectedDepositMethods.includes(m.name)) {
                        methodsToRemove = methods
                        methodsToAdd = []
                      }

                      setSelectedDepositMethods(
                        [...selectedDepositMethods.filter(m => !methodsToRemove.includes(m)), ...methodsToAdd]
                      )
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

                      <Checkbox checked={selectedDepositMethods.includes(m.name)} />
                      <div
                        style={{
                          height: 25,
                          width: 25,
                          backgroundImage: `url(${m.logo})`,
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                          position: 'relative'
                        }} />

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 14 }}>{m.name}</span>
                        <span style={{ fontSize: 12, opacity: 0.5 }}>{m.description}</span>
                      </div>
                    </div>
                  </DropdownItem>
                  {
                    m.subMethods?.map(m => {
                      const method = m.method ? `${m.method.network}_${m.method.token}` : m.name

                      return <div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          height: 45
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%', }}>
                            <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                            <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8, }} />
                          </div>

                          <DropdownItem
                            onClick={() => {
                              const methods = filter(flattenDeep([method, m.subMethods?.map(n => `${n.method.network}_${n.method.token}`)]), m => !!m);
                              let methodsToAdd = methods, methodsToRemove = methods

                              if (selectedDepositMethods.includes(method)) {
                                methodsToRemove = methods
                                methodsToAdd = []
                              }

                              setSelectedDepositMethods(
                                [...selectedDepositMethods.filter(m => !methodsToRemove.includes(m)), ...methodsToAdd]
                              )
                            }}
                            style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Checkbox checked={selectedDepositMethods.includes(method)} />

                              <div
                                style={{
                                  height: 25,
                                  width: 25,
                                  backgroundImage: `url(${m.logo})`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundSize: 'contain',
                                  backgroundPosition: 'center',
                                  position: 'relative'
                                }} />

                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: 14 }}>{m.name}</span>
                                <span style={{ fontSize: 12, opacity: 0.5 }}>{m.ticker}</span>
                              </div>

                            </div>
                          </DropdownItem>



                        </div>

                        {
                          m.subMethods?.map(n => {
                            const method = `${n.method.network}_${n.method.token}`;

                            return <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              height: 48
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8, }} />
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: 25, height: '100%' }}>
                                <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8, }} />
                                <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8, }} />
                              </div>

                              <DropdownItem
                                onClick={() => {
                                  const methods = [method];
                                  const methodsToRemove = selectedDepositMethods.filter(m => methods.includes(m))
                                  const methodsToAdd = methods.filter(m => !selectedDepositMethods.includes(m))

                                  setSelectedDepositMethods(
                                    [...selectedDepositMethods.filter(m => !methodsToRemove.includes(m)), ...methodsToAdd]
                                  )
                                }}
                                style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <Checkbox checked={selectedDepositMethods.includes(method)} />

                                  <div
                                    style={{
                                      height: 25,
                                      width: 25,
                                      backgroundImage: `url(${n.logo})`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundSize: 'contain',
                                      backgroundPosition: 'center',
                                      position: 'relative'
                                    }} />

                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: 14 }}>{n.name}</span>
                                    <span style={{ fontSize: 12, opacity: 0.5 }}>{n.ticker}</span>
                                  </div>
                                </div>
                              </DropdownItem>

                            </div>
                          })}

                      </div>
                    }
                    )}
                </>
              )
          } />
      </div>
    </>
  )

  const renderBankForm = (): ReactElement => (
    <>
      <TextInputWithLabel
        label='Account nickname'
        placeholder='Enter a nickname for this account'
        disabled={isLoading}
        required
        value={accountName}
        onChange={e => {
          const el = (e.target as HTMLInputElement)
          const value = el.value
          setAccountName(value)
        }}
      />

      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
        <InputLabel>Account type</InputLabel>
        <Dropdown
          key={fiatAccountType}
          disabled={isLoading}
          selectedContent={
            fiatAccountType
              ? <div style={{
                fontSize: 14,
                alignItems: 'center',
                display: 'flex',
                gap: 10
              }}>
                <img
                  style={{
                    width: 20,
                    maxHeight: 20,
                    maxWidth: 20,
                  }}
                  src={`https://pay.radom.com/flags/${fiatAccountType == FiatAccountType.USD ? 'us' : 'eu'}.svg`}
                />
                {fiatAccountType}
              </div>
              : <span style={{ fontSize: 14 }}>Select bank account type</span>
          }
          dropdownContent={
            [FiatAccountType.USD, FiatAccountType.EUR].map(c =>
              <DropdownItem
                key={c}
                onClick={() => {
                  setFiatAccountType(c)
                }}
                style={{
                  fontSize: 14,
                  alignItems: 'center',
                  display: 'flex',
                  gap: 10
                }}>
                <img
                  style={{
                    width: 20,
                    maxHeight: 20,
                    maxWidth: 20,
                  }}
                  src={`https://pay.radom.com/flags/${c == FiatAccountType.USD ? 'us' : 'eu'}.svg`}
                />


                {c}
              </DropdownItem>
            )
          } />
      </div>
    </>
  )

  return <Modal {...props} innerStyles={{ width: 550 }}>
    <div style={{
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 10,
      padding: '0px 30px'
    }}>
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 20 }}>
        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Account type</InputLabel>
          <div style={{ fontSize: 14, display: 'flex', gap: 20 }}>
            <SecondaryButton
              disabled={isLoading}
              onClick={() => setAccountType(AccountType.Crypto)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: accountType === AccountType.Crypto ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox isRadio checked={accountType === AccountType.Crypto} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Crypto</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                  Deposit into a crypto address
                </span>
              </div>
            </SecondaryButton>

            <SecondaryButton
              // disabled
              onClick={() => setAccountType(AccountType.Fiat)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: accountType === AccountType.Fiat ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox isRadio checked={accountType === AccountType.Fiat} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Fiat</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                  Deposit into a bank account
                </span>
              </div>
            </SecondaryButton>

          </div>
        </div>

        {accountType === AccountType.Crypto && renderCryptoForm()}

        {
          accountType === AccountType.Fiat &&
          User.organization?.verificationStatus === 'Verified' &&
          renderBankForm()
        }
      </div>
    </div>

    {
      accountType === AccountType.Crypto && <div style={{
        padding: 30,
        borderRadius: 15,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0
      }}>
        <PrimaryButton
          isLoading={isLoading}
          disabled={!accountName || selectedDepositMethods.length <= 0}
          style={{ width: '100%' }}
          onClick={() => {
            createAccount()
          }}>
          Create account
        </PrimaryButton>
      </div>
    }

    {
      accountType === AccountType.Fiat &&
      User.organization?.verificationStatus === 'Verified' &&
      <div style={{
        padding: 30,
        borderRadius: 15,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        position: 'sticky',
        bottom: 0,
        background: 'linear-gradient(transparent, white 20%)'
      }}>
        <PrimaryButton
          disabled={!accountName || !fiatAccountType}
          isLoading={isLoading}
          style={{ width: '100%' }}
          onClick={() => {
            createAccount()
          }}>
          Create account
        </PrimaryButton>
      </div>
    }
  </Modal>
})

export default CreateDepositAccountModal
